import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Card from 'react-bootstrap/Card';
import { Form, Tab, Tabs } from 'react-bootstrap';

import SpinnerButton from '../../../common/components/Button';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { CreateUpdateHealthcareFacilityDto } from '../../../common/spdCore/autogenerated/spdApiClient';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { useSpdCore } from '../../../common/hooks/useSpdCore';

import { HealthcareFacilityGeneralTab } from './HealthcareFacilityGeneralTab';
import { HealthcareFacilityRegisterDataTab } from './HealthcareFacilityDetailedTab';
import { HealthcareFacilityAdvancedTab } from './HealthcareFacilityAdvancedTab';
import { OpenCloseTimeEditor } from './OpenCloseTimeEditor';

interface HealthcareFacilityEditorProps {
  id?: string;
  healthcareFacility: CreateUpdateHealthcareFacilityDto;
  handleClose?: () => void;
  show?: boolean;
  isHistorical?: boolean;
  onCompleted: (
    healthcareFacility: CreateUpdateHealthcareFacilityDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const HealthcareFacilityEditor: React.FC<
  HealthcareFacilityEditorProps
> = props => {
  const [healthcareFacility, setHealthcareFacility] = useState(
    props.healthcareFacility,
  );
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [validFrom, setValidFrom] = useState<Date | null>(null);

  const spd = useSpdCore();

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setHealthcareFacility(props.healthcareFacility);
      setValidFrom(null);
    } else {
      setHealthcareFacility({});
      setSaveErrors([]);
      setShowSaveErrors(false);
      setValidFrom(null);
    }
  }, [props.show, props.healthcareFacility, props.id]);

  const createHealthcareFacility = async () => {
    setIsSaving(true);
    props
      .onCompleted({ ...healthcareFacility, validFrom }, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
      })
      .catch(error => {
        console.error('Error during add healthcareFacility:', error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const propertyChange = (obj: Partial<CreateUpdateHealthcareFacilityDto>) => {
    setHealthcareFacility({ ...healthcareFacility, ...obj });
  };

  const handleConfirmNewData = async () => {
    propertyChange({
      createNewHealthcareFacilityData: true,
    });
  };

  const handleCancelNewData = () => {
    propertyChange({
      createNewHealthcareFacilityData: false,
    });
    setValidFrom(null);
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id ? 'Edycja placówki' : 'Dodawanie nowej placówki'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="mb-2">
            <Card.Body>
              <Form className="d-grid gap-3">
                <Tabs
                  defaultActiveKey="general"
                  id="healthcareFacilityTabs"
                  className="mb-2"
                >
                  <Tab
                    eventKey="general"
                    title="Dane podstawowe"
                    className="mb-2"
                  >
                    <HealthcareFacilityGeneralTab
                      healthcareFacility={healthcareFacility}
                      propertyChange={propertyChange}
                      id={props.id}
                    />
                  </Tab>
                  <Tab
                    eventKey="detailed"
                    title="Dane rejestrowe"
                    className="mb-2"
                  >
                    <HealthcareFacilityRegisterDataTab
                      healthcareFacility={healthcareFacility}
                      propertyChange={propertyChange}
                    />
                  </Tab>
                  <Tab
                    eventKey="openCloseTimes"
                    title="Godziny otwarcia"
                    className="mb-2"
                  >
                    <OpenCloseTimeEditor
                      healthcareFacility={healthcareFacility}
                      propertyChange={propertyChange}
                    />
                  </Tab>
                  {!props.isHistorical && (
                    <Tab
                      eventKey="advanced"
                      title="Zaawansowane"
                      className="mb-2"
                    >
                      <HealthcareFacilityAdvancedTab
                        healthcareFacility={healthcareFacility}
                        validFrom={validFrom}
                        propertyChange={propertyChange}
                        onConfirmNewData={handleConfirmNewData}
                        onCancelNewData={handleCancelNewData}
                        onValidFromChange={setValidFrom}
                      />
                    </Tab>
                  )}
                </Tabs>
              </Form>
            </Card.Body>
          </Card>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
        </Modal.Body>
        <Modal.Footer>
          {props.id && (
            <LastEditorInfo
              id={props.id}
              method={spd.healthCareFacilities.getLastEditor}
            />
          )}
          <Button variant="outline-secondary" onClick={props.handleClose}>
            Zamknij
          </Button>
          <SpinnerButton
            variant="outline-primary"
            isLoading={isSaving}
            onClick={createHealthcareFacility}
          >
            Zapisz
          </SpinnerButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};
